"use client";
import { useGlobalModalContext } from "@components/GlobalModalProvider/GlobalModalProvider";
import { FiltersContext } from "@components/commerce/product-listing/filters/context/FiltersContext";
import { FilterButton } from "@components/commerce/product-listing/filters/filter-button/FilterButton";
import { ToolbarFilterItem } from "@components/commerce/product-listing/filters/toolbar/item/ToolbarFilterItem";
import SortBy from "@components/commerce/product-listing/filters/toolbar/item/sortBy/SortBy";
import type { IPartFilter } from "@contentful-api/types/contentful";
import { useNormalizedInfiniteHits } from "@hooks/useNormalizedInfiniteHits";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Divider } from "@ui/components/data-display/divider/Divider";
import { Flex } from "@ui/components/layout/flex/Flex";
import { Grid } from "@ui/components/layout/grid/Grid";
import { ECCOIcon } from "@ui/components/media-and-icons/ecco-icon/ECCOIcon";
import { Collapse } from "@ui/components/other/transitions/Collapse";
import { useOutsideClick } from "@ui/hooks/useOutsideClick";
import dynamic from "next/dynamic";
import { memo, useContext, useEffect, useRef, useState } from "react";
import {
    dividerStyles,
    filterButtonStyles,
    filtersContainerStyles,
    filtersDialogCollapse,
    overlayStyle,
    resultsStyles,
    wrapperStyles,
} from "./FiltersToolbarStyles";
import { Overlay } from "./overlay/Overlay";
import { useStore } from "@store/store";

const SelectedFilters = dynamic(
    () => import("@components/commerce/product-listing/filters/toolbar/selected/SelectedFilters"),
    { ssr: false }
);

const FiltersDialog = dynamic(
    () => import("@components/commerce/product-listing/filters/dialog/FiltersDialog"),
    { ssr: false }
);

export const FILTER_DIALOG_TOGGLE_BUTTON_ID = "filter-dialog-toggle-button";

interface FiltersToolbarProps {
    listOfFilters: IPartFilter[];
    locale: string;
}

export const FiltersToolbar = memo(({ listOfFilters, locale }: FiltersToolbarProps) => {
    const filterRef = useRef<HTMLDivElement>(null);
    const filterDialogRef = useRef<HTMLDivElement>(null);
    const filterButtonRef = useRef<HTMLButtonElement>(null);
    const [dividerVisible, setDividerVisible] = useState<boolean>(false);
    const setResultsCount = useStore((state) => state.setResultsCount);
    const { get: getMicrocopy } = useMicrocopy();
    const { setFiltersDialogOpened, filtersDialogOpened, closeFiltersDialog, mobileSortByOpen } =
        useContext(FiltersContext);
    const { numberOfHits } = useNormalizedInfiniteHits();
    const { modalType } = useGlobalModalContext();

    const resultsCount = numberOfHits || 0;
    const changeDivider = () => {
        filterRef.current?.getBoundingClientRect().top <= 72
            ? setDividerVisible(true)
            : setDividerVisible(false);
    };
    const FilterButtonIcon = filtersDialogOpened ? (
        <ECCOIcon aria-hidden name="close" size="sm" />
    ) : (
        <ECCOIcon aria-hidden name="filter" size="sm" />
    );

    useEffect(() => {
        setResultsCount(resultsCount);
        window.addEventListener("scroll", changeDivider);
        return () => {
            window.removeEventListener("scroll", changeDivider);
        };
    }, [dividerVisible, numberOfHits]);

    useOutsideClick({
        ref: filterDialogRef,
        handler: (event) => {
            if ((event.target as HTMLElement).id === FILTER_DIALOG_TOGGLE_BUTTON_ID) return;
            if (event.target !== filterDialogRef.current) {
                !modalType && filtersDialogOpened && closeFiltersDialog();
                filterButtonRef.current.blur();
            }
        },
    });

    const handleFilterButtonClick = () => {
        setFiltersDialogOpened();
        if (filtersDialogOpened) return;
        filterButtonRef.current?.scrollIntoView();
    };

    useEffect(() => {
        if (filtersDialogOpened && filterDialogRef.current) {
            filterDialogRef.current.focus();
        }
    }, [filtersDialogOpened]);

    return (
        <Flex sx={wrapperStyles} ref={filterRef}>
            <Grid sx={filtersContainerStyles}>
                <Flex>
                    <Flex data-testid="product-filters" sx={filterButtonStyles}>
                        {listOfFilters.map((filter, index) => (
                            <ToolbarFilterItem key={filter?.sys?.id || index} {...filter?.fields} />
                        ))}
                    </Flex>
                    {filtersDialogOpened && (
                        <Collapse
                            ref={filterDialogRef}
                            style={filtersDialogCollapse}
                            in={filtersDialogOpened}
                            tabIndex={-1}
                        >
                            <FiltersDialog listOfFilters={listOfFilters} />
                        </Collapse>
                    )}
                    <Collapse in={filtersDialogOpened || mobileSortByOpen} animateOpacity>
                        <Overlay sx={overlayStyle} />
                    </Collapse>
                </Flex>
                <Flex sx={resultsStyles}>
                    <FilterButton
                        ref={filterButtonRef}
                        onClick={handleFilterButtonClick}
                        id={FILTER_DIALOG_TOGGLE_BUTTON_ID}
                        icon={FilterButtonIcon}
                        text={getMicrocopy(CommonCMS.page, CommonCMS.button.allFilters)}
                        iconPlacement="right"
                        type="top"
                    />
                    <SortBy locale={locale} />
                </Flex>
            </Grid>
            <SelectedFilters locale={locale} />
            {dividerVisible && <Divider sx={dividerStyles} pt={[4, 6]} borderColor="transparent" />}
        </Flex>
    );
});

FiltersToolbar.displayName = "FiltersToolbar";
